import { Component, effect, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, signal, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Temporal } from '@js-temporal/polyfill';

@Component({
  selector: 'app-metro-date-input-picker',
  templateUrl: './metro-date-input-picker.component.html',
  standalone:false
})
export class MetroDateInputPickerComponent implements OnInit, AfterViewInit {

 @Input() label?: string;
  @Input() placeholder = 'dd/mm/yyyy';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() disabled = false;
  @Input() helper?: string;
  @Input() errorMessage = 'Formato de fecha inválido';

  @Input() modeInput: 'single' | 'range' = 'range';
  @Input() selectedDateInput?: Temporal.PlainDate;
  @Input() startDateInput?: Temporal.PlainDate;
  @Input() endDateInput?: Temporal.PlainDate;

  @Output() dateSelectedChange = new EventEmitter<Temporal.PlainDate>();
  @Output() rangeSelectedChange = new EventEmitter<{ start: Temporal.PlainDate; end: Temporal.PlainDate }>();

  mode = signal<'single' | 'range'>('range');
  calendarVisible = signal(false);
  inputValue = signal('');
  calendarPosition = signal<'bottom' | 'top'>('bottom');
  calendarAlignment = signal<'left' | 'right'>('left');

  iconCalendar = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M128 0c17.7 0 32 14.3 32 32l0 32 128 0 0-32c0-17.7 14.3-32 32-32s32 14.3 32 32l0 32 48 0c26.5 0 48 21.5 48 48l0 48H0l0-48c0-26.5 21.5-48 48-48l48 0 0-32c0-17.7 14.3-32 32-32zM0 192l448 0 0 272c0 26.5-21.5 48-48 48L48 512c-26.5 0-48-21.5-48-48L0 192zm64 80l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm128 0l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0zM64 400l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0zm112 16l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16z"/></svg>`;

  startDate = signal<Temporal.PlainDate | null>(null);
  endDate = signal<Temporal.PlainDate | null>(null);
  selectedDate = signal<Temporal.PlainDate | null>(null);

  @ViewChild('calendarRef') calendarRef!: ElementRef;
  @ViewChild('inputRef') inputRef!: ElementRef;

  constructor(private cdr: ChangeDetectorRef) {
    effect(() => {
      if (this.mode() === 'single' && this.selectedDate()) {
        this.inputValue.set(this.formatDate(this.selectedDate()!));
      }
      if (this.mode() === 'range' && this.startDate() && this.endDate()) {
        this.inputValue.set(
          `${this.formatDate(this.startDate()!)} - ${this.formatDate(this.endDate()!)}`
        );
      }
    });

    // Effect para ajustar posición cuando el calendario se muestra
    effect(() => {
      if (this.calendarVisible()) {
        setTimeout(() => this.adjustCalendarPosition(), 0);
      }
    });
  }

  ngOnInit(): void {
    this.mode.set(this.modeInput);

    if (this.modeInput === 'single' && this.selectedDateInput) {
      this.selectedDate.set(this.selectedDateInput);
    }

    if (this.modeInput === 'range' && this.startDateInput && this.endDateInput) {
      this.startDate.set(this.startDateInput);
      this.endDate.set(this.endDateInput);
    }
  }

  ngAfterViewInit(): void {
  }

  adjustCalendarPosition(): void {
    if (!this.calendarRef?.nativeElement || !this.inputRef?.nativeElement) {
      return;
    }

    // Usar requestAnimationFrame para asegurar que el DOM esté actualizado
    requestAnimationFrame(() => {
      const calendarEl = this.calendarRef.nativeElement;
      const inputEl = this.inputRef.nativeElement;
      const inputRect = inputEl.getBoundingClientRect();
      const calendarRect = calendarEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Calcular espacio disponible (con margen de seguridad)
      const margin = 10;
      const estimatedCalendarHeight = 400; // Altura aproximada del calendario
      const estimatedCalendarWidth = 650; // Ancho aproximado del calendario (dos meses)
      
      const spaceBelow = viewportHeight - inputRect.bottom - margin;
      const spaceAbove = inputRect.top - margin;
      const spaceRight = viewportWidth - inputRect.left - margin;
      const spaceLeft = inputRect.left - margin;

      // Ajustar posición vertical
      // Si no hay suficiente espacio abajo y hay más espacio arriba, mostrar arriba
      if (spaceBelow < estimatedCalendarHeight && spaceAbove > spaceBelow) {
        this.calendarPosition.set('top');
      } else {
        this.calendarPosition.set('bottom');
      }

      // Ajustar posición horizontal
      // Si no hay suficiente espacio a la derecha y hay más espacio a la izquierda, alinear a la derecha
      if (spaceRight < estimatedCalendarWidth && spaceLeft > spaceRight) {
        this.calendarAlignment.set('right');
      } else {
        this.calendarAlignment.set('left');
      }

      this.cdr.detectChanges();
    });
  }

  toggleCalendar(): void {
    this.calendarVisible.update(v => !v);
    if (!this.calendarVisible()) {
      return;
    }
    // Ajustar posición después de que el calendario se muestre
    // Usar un pequeño delay para asegurar que el DOM se haya actualizado
    setTimeout(() => this.adjustCalendarPosition(), 10);
  }

  onKeyDown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab',
      'Home', 'End'
    ];

    if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  onSingleSelected(date: Temporal.PlainDate) {
    this.selectedDate.set(date);
    this.calendarVisible.set(false);
    this.dateSelectedChange.emit(date);
  }

  onRangeSelected(range: { start: Temporal.PlainDate; end: Temporal.PlainDate }) {
    this.startDate.set(range.start);
    this.endDate.set(range.end);
    this.calendarVisible.set(false);
    this.rangeSelectedChange.emit(range);
  }

  onInputChange(value: string) {
    let numericValue = value.replace(/[^0-9]/g, '');

    if (this.mode() === 'single') {
      if (numericValue.length > 2 && numericValue.length <= 4) {
        numericValue = numericValue.slice(0, 2) + '/' + numericValue.slice(2);
      } else if (numericValue.length > 4) {
        numericValue = numericValue.slice(0, 2) + '/' + numericValue.slice(2, 4) + '/' + numericValue.slice(4, 8);
      }
    }

    if (this.mode() === 'range') {
      if (numericValue.length <= 8) {
        if (numericValue.length > 2 && numericValue.length <= 4) {
          numericValue = numericValue.slice(0, 2) + '/' + numericValue.slice(2);
        } else if (numericValue.length > 4) {
          numericValue = numericValue.slice(0, 2) + '/' + numericValue.slice(2, 4) + '/' + numericValue.slice(4, 8);
        }
      } else {
        let firstPart = numericValue.slice(0, 8);
        let secondPart = numericValue.slice(8);

        firstPart = firstPart.slice(0, 2) + '/' + firstPart.slice(2, 4) + '/' + firstPart.slice(4, 8);

        if (secondPart.length > 2 && secondPart.length <= 4) {
          secondPart = secondPart.slice(0, 2) + '/' + secondPart.slice(2);
        } else if (secondPart.length > 4) {
          secondPart = secondPart.slice(0, 2) + '/' + secondPart.slice(2, 4) + '/' + secondPart.slice(4, 8);
        }

        numericValue = firstPart + ' - ' + secondPart;
      }
    }

    this.inputValue.set(numericValue);

    if (this.mode() === 'single' && this.validateSingleDate(numericValue)) {
      const parsed = this.parseDate(numericValue);
      if (parsed) this.selectedDate.set(parsed);
      this.dateSelectedChange.emit(parsed);
    }

    if (this.mode() === 'range' && this.validateRangeDate(numericValue)) {
      const [startStr, endStr] = numericValue.split(' - ');
      const start = this.parseDate(startStr);
      const end = this.parseDate(endStr);
      if (start && end) {
        this.startDate.set(start);
        this.endDate.set(end);
        this.rangeSelectedChange.emit({ start, end });
      }
    }
  }

  formatDate(date: Temporal.PlainDate): string {
    const day = String(date.day).padStart(2, '0');
    const month = String(date.month).padStart(2, '0');
    return `${day}/${month}/${date.year}`;
  }

  parseDate(str: string): Temporal.PlainDate | null {
    const [day, month, year] = str.split('/');
    try {
      return Temporal.PlainDate.from({ day: +day, month: +month, year: +year });
    } catch {
      return null;
    }
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    if (!this.calendarVisible()) return;

    const clickedElement = event.target as HTMLElement;
    const calendarEl = this.calendarRef?.nativeElement;
    const inputEl = this.inputRef?.nativeElement;

    if (calendarEl?.contains(clickedElement) || inputEl?.contains(clickedElement)) {
      return;
    }

    if (this.mode() === 'range' && this.startDate() && !this.endDate()) {
      return;
    }

    this.calendarVisible.set(false);
  }

  isValidDateFormat(): boolean {
    const value = this.inputValue();
    if (this.mode() === 'single') return this.validateSingleDate(value);
    if (this.mode() === 'range') return this.validateRangeDate(value);
    return false;
  }

  validateSingleDate(value: string): boolean {
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = value.match(regex);
    if (!match) return false;

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    return this.isValidDayMonth(day, month);
  }

  validateRangeDate(value: string): boolean {
    const regex = /^(\d{2})\/(\d{2})\/(\d{4}) - (\d{2})\/(\d{2})\/(\d{4})$/;
    const match = value.match(regex);
    if (!match) return false;

    const startDay = parseInt(match[1], 10);
    const startMonth = parseInt(match[2], 10);
    const endDay = parseInt(match[4], 10);
    const endMonth = parseInt(match[5], 10);

    return this.isValidDayMonth(startDay, startMonth) && this.isValidDayMonth(endDay, endMonth);
  }

  isValidDayMonth(day: number, month: number): boolean {
    return day >= 1 && day <= 31 && month >= 1 && month <= 12;
  }

}
